/**
 * ValidationSummaryModal
 *
 * Displays a list of validation errors with clickable links that scroll
 * to the corresponding question. Appears when user attempts to submit
 * with validation errors.
 *
 * @module components/survey/ValidationSummaryModal
 */

import { useMemo } from 'react';

// MUI Components
import Dialog from '@mui/material/Dialog';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import DialogActions from '@mui/material/DialogActions';
import List from '@mui/material/List';
import ListItemButton from '@mui/material/ListItemButton';
import ListItemIcon from '@mui/material/ListItemIcon';
import ListItemText from '@mui/material/ListItemText';
import Button from '@mui/material/Button';
import Typography from '@mui/material/Typography';
import Box from '@mui/material/Box';
import Chip from '@mui/material/Chip';
import Divider from '@mui/material/Divider';

// Icons
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
import NavigateNextIcon from '@mui/icons-material/NavigateNext';

// MUI Utilities
import { alpha } from '@mui/material/styles';

// i18n
import { useSurveyTranslation, useCommonTranslation } from '@/i18n/hooks/useTypedTranslation';

// Types
import type { Question } from '@/types';
import type { ValidationError } from '@/utils/surveyHelpers';

// =============================================================================
// TYPE DEFINITIONS
// =============================================================================

/**
 * Props for ValidationSummaryModal component
 */
export interface ValidationSummaryModalProps {
  /** Whether the modal is open */
  open: boolean;
  /** Callback when modal should close */
  onClose: () => void;
  /** Array of validation errors with translation keys */
  errors: ValidationError[];
  /** All visible questions (for display order and text) */
  questions: Question[];
  /** Callback when user clicks to navigate to a question */
  onNavigateToQuestion: (questionId: number) => void;
}

// =============================================================================
// HELPER FUNCTIONS
// =============================================================================

/**
 * Truncates text to a maximum length with ellipsis
 */
function truncateText(text: string, maxLength: number = 60): string {
  if (text.length <= maxLength) return text;
  return text.substring(0, maxLength).trim() + '...';
}

/**
 * Gets the question number from a list of questions
 */
function getQuestionNumber(questionId: number, questions: Question[]): number {
  const index = questions.findIndex((q) => q.id === questionId);
  return index >= 0 ? index + 1 : 0;
}

// =============================================================================
// COMPONENT
// =============================================================================

/**
 * Modal component that displays validation errors with navigation links.
 *
 * @example
 * ```tsx
 * <ValidationSummaryModal
 *   open={showValidationModal}
 *   onClose={() => setShowValidationModal(false)}
 *   errors={validationErrors}
 *   questions={visibleQuestions}
 *   onNavigateToQuestion={(questionId) => {
 *     document.getElementById(`question-${questionId}`)?.scrollIntoView({
 *       behavior: 'smooth',
 *       block: 'center',
 *     });
 *     setShowValidationModal(false);
 *   }}
 * />
 * ```
 */
export function ValidationSummaryModal({
  open,
  onClose,
  errors,
  questions,
  onNavigateToQuestion,
}: ValidationSummaryModalProps) {
  const { t } = useSurveyTranslation();
  const { t: tCommon } = useCommonTranslation();

  // Build sorted list of errors with question context
  const errorList = useMemo(() => {
    return errors
      .map((error) => {
        const question = questions.find((q) => q.id === error.questionId);
        const questionNumber = getQuestionNumber(error.questionId, questions);

        return {
          ...error,
          questionText: question?.text || 'Unknown question',
          questionNumber,
          displayOrder: question?.displayOrder ?? 999,
        };
      })
      .sort((a, b) => a.displayOrder - b.displayOrder);
  }, [errors, questions]);

  const errorCount = errorList.length;

  /**
   * Handle click on an error item - navigate and close modal
   */
  const handleErrorClick = (questionId: number) => {
    onNavigateToQuestion(questionId);
    onClose();
  };

  /**
   * Get translated error message
   */
  const getTranslatedMessage = (error: ValidationError): string => {
    // Try to translate using the key with params
    // Use type assertion to handle the dynamic key and params
    const translated = error.params
      ? String(t(error.messageKey as never, error.params as never))
      : String(t(error.messageKey as never));
    // If translation returns the key itself, use fallback
    if (translated === error.messageKey) {
      return error.fallbackMessage;
    }
    return translated;
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="sm"
      fullWidth
      aria-labelledby="validation-summary-title"
      aria-describedby="validation-summary-description"
    >
      {/* Header */}
      <DialogTitle id="validation-summary-title" sx={{ pb: 1 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
          <Box
            sx={{
              width: 40,
              height: 40,
              borderRadius: 1,
              bgcolor: alpha('#ef4444', 0.1),
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
            }}
          >
            <ErrorOutlineIcon sx={{ color: 'error.main', fontSize: 24 }} />
          </Box>
          <Box sx={{ flex: 1 }}>
            <Typography variant="h6" component="span" sx={{ fontWeight: 600 }}>
              {t('public.validation.incompleteQuestions')}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              {t('public.validation.modalDescription')}
            </Typography>
          </Box>
          <Chip
            label={errorCount}
            size="small"
            color="error"
            sx={{ fontWeight: 700 }}
          />
        </Box>
      </DialogTitle>

      <Divider />

      {/* Error List */}
      <DialogContent sx={{ p: 0 }} id="validation-summary-description">
        <List disablePadding>
          {errorList.map((error, index) => (
            <ListItemButton
              key={error.questionId}
              onClick={() => handleErrorClick(error.questionId)}
              sx={{
                py: 1.5,
                px: 3,
                borderBottom: index < errorList.length - 1 ? 1 : 0,
                borderColor: 'divider',
                '&:hover': {
                  bgcolor: alpha('#ef4444', 0.04),
                },
              }}
            >
              <ListItemIcon sx={{ minWidth: 40 }}>
                <Box
                  sx={{
                    width: 28,
                    height: 28,
                    borderRadius: 0.75,
                    bgcolor: alpha('#ef4444', 0.1),
                    color: 'error.main',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    fontWeight: 700,
                    fontSize: 12,
                  }}
                >
                  Q{error.questionNumber}
                </Box>
              </ListItemIcon>
              <ListItemText
                primary={
                  <Typography
                    variant="body2"
                    sx={{ fontWeight: 500, color: 'text.primary' }}
                  >
                    {truncateText(error.questionText)}
                  </Typography>
                }
                secondary={
                  <Typography variant="caption" color="error.main">
                    {getTranslatedMessage(error)}
                  </Typography>
                }
              />
              <NavigateNextIcon sx={{ color: 'text.secondary', fontSize: 20 }} />
            </ListItemButton>
          ))}
        </List>
      </DialogContent>

      <Divider />

      {/* Actions */}
      <DialogActions sx={{ px: 3, py: 2 }}>
        <Button onClick={onClose} color="inherit">
          {tCommon('actions.close')}
        </Button>
        <Button
          variant="contained"
          onClick={() => {
            if (errorList.length > 0) {
              handleErrorClick(errorList[0].questionId);
            }
          }}
          startIcon={<NavigateNextIcon />}
        >
          {t('public.validation.goToFirstError')}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

export default ValidationSummaryModal;
